<template>
  <v-col cols="12" class="pb-0">
    <PaymentMethodListSkeleton v-if="(isLoading || isRetrying) && !hasError" />

    <PaymentMethodListError v-else-if="hasError" :has-error="hasError" @retry="reloadPaymentMethods" />

    <div v-else>
      <div class="d-flex justify-space-between align-center mb-3">
        <div class="text-overline--small neutral--text text--darken-1">
          {{ $t('fields.account.billing.payment.payment_methods_section') }}
        </div>

        <v-btn
          v-if="isTeamLead && defaultPaymentMethod"
          text
          :ripple="false"
          color="primary"
          x-small
          :disabled="isSettingDefault"
          data-test="payment-methods-add-new"
          @click="onAddNewClick"
        >
          <span class="navy--text"> + {{ $t('fields.account.billing.payment.payment_methods_add_new') }} </span>
        </v-btn>
      </div>

      <AlertSubUserPayment
        v-if="!isTeamLead"
        :message="$t('fields.account.billing.payment.payment_methods_read_only')"
        size="small"
        class="mb-3"
        data-test="payment-methods-read-only-banner"
      />

      <PaymentMethodDetails
        v-if="defaultPaymentMethod"
        :method="defaultPaymentMethod"
        variant="default"
        :highlight="isHighlighted(defaultPaymentMethod)"
        class="mb-3"
      />

      <PaymentMethodListEmpty
        v-else
        :is-team-lead="isTeamLead"
        :disabled="isSettingDefault"
        class="mb-3"
        @add="onAddNewClick"
      />

      <OtherSavedPaymentMethods
        v-if="otherSavedPaymentMethods.length > 0"
        v-model="otherSavedMethodsPanel"
        :methods="otherSavedPaymentMethods"
        :is-team-lead="isTeamLead"
        :setting-default-method-id="settingDefaultMethodId"
        :actions-disabled="isSettingDefault"
        :highlighted-method-id="highlightedMethodId"
        @set-default="onSetDefaultPaymentMethod"
        @remove="onRemovePaymentMethod"
      />
    </div>

    <payment-method-edit
      :open="isAddDialogShown"
      :wizard-step="$route.query.step"
      :redirect-status="$route.query.redirect_status"
      @cancel="isAddDialogShown = false"
      @update-payment-method="onPaymentMethodAdded"
      @update-billing-address="onUpdatePaymentAddress"
    />

    <remove-payment-method-dialog
      :open="isRemoveDialogShown"
      :method="paymentMethodToRemove"
      @cancel="closeRemoveDialog"
      @success="closeRemoveDialog"
    />

    <change-default-payment-method-dialog
      :open="isChangeDefaultDialogShown"
      :method="pendingSetDefaultMethod"
      :is-confirming="isSettingDefault"
      @cancel="closeChangeDefaultDialog"
      @confirm="onChangeDefaultConfirmed"
    />
  </v-col>
</template>

<script lang="ts">
import type { SavedPaymentMethod } from '@/classes/stripe';
import PaymentMethodEdit from '@/components/Modals/Account/PaymentMethodEdit.vue';
import ChangeDefaultPaymentMethodDialog from '@/components/Modals/Account/ChangeDefaultPaymentMethodDialog.vue';
import RemovePaymentMethodDialog from '@/components/Modals/Account/RemovePaymentMethodDialog.vue';
import OtherSavedPaymentMethods from '@/components/Subscription/OtherSavedPaymentMethods.vue';
import PaymentMethodDetails from '@/components/Subscription/PaymentMethodDetails.vue';
import PaymentMethodListEmpty from '@/components/Subscription/PaymentMethodListEmpty.vue';
import PaymentMethodListError from '@/components/Subscription/PaymentMethodListError.vue';
import PaymentMethodListSkeleton from '@/components/Subscription/PaymentMethodListSkeleton.vue';
import AlertSubUserPayment from '@/components/Alerts/AlertSubUserPayment.vue';
import {
  clearAsyncPaymentMethodAddQueryParams,
  isFailedAsyncPaymentMethodAddReturn,
  isReturningFromAsyncPaymentMethodAdd,
  resolveAddedPaymentMethodIdFromQuery,
} from '@/Services/PaymentMethod/PaymentMethodRedirectReturn';
import OptimizelyService from '@/Services/OptimizelyService';
import { Component, Emit, Vue } from 'vue-property-decorator';
import { mapGetters } from 'vuex';

@Component({
  components: {
    AlertSubUserPayment,
    PaymentMethodEdit,
    ChangeDefaultPaymentMethodDialog,
    RemovePaymentMethodDialog,
    OtherSavedPaymentMethods,
    PaymentMethodDetails,
    PaymentMethodListEmpty,
    PaymentMethodListError,
    PaymentMethodListSkeleton,
  },
  computed: {
    ...mapGetters('auth', ['isTeamLead']),
    ...mapGetters('subscription', ['defaultPaymentMethod', 'otherSavedPaymentMethods']),
  },
})
export default class PaymentMethodList extends Vue {
  isAddDialogShown = false;
  isRemoveDialogShown = false;
  isChangeDefaultDialogShown = false;
  paymentMethodToRemove: SavedPaymentMethod | null = null;
  pendingSetDefaultMethod: SavedPaymentMethod | null = null;
  settingDefaultMethodId: string | null = null;
  otherSavedMethodsPanel: number | null = null;
  highlightedMethodId: string | null = null;

  private highlightTimeout: number | null = null;
  /** Keep the freshly added card highlighted for ~3s before it fades back. */
  private static readonly HIGHLIGHT_DURATION_MS = 3500;

  declare isTeamLead: boolean;
  declare defaultPaymentMethod: SavedPaymentMethod | null;
  declare otherSavedPaymentMethods: SavedPaymentMethod[];

  get isLoading(): boolean {
    return this.$store.state.subscription.loadingPaymentMethods;
  }

  get hasError(): boolean {
    return this.$store.state.subscription.paymentMethodsError;
  }

  get isRetrying(): boolean {
    return this.$store.state.subscription.retryingPaymentMethods;
  }

  get isSettingDefault(): boolean {
    return this.settingDefaultMethodId !== null;
  }

  /**
   * Only highlight a method that was just added. Requires a non-null highlight
   * id so a missing selection never falsely matches a method id.
   */
  isHighlighted(method: SavedPaymentMethod): boolean {
    return this.highlightedMethodId !== null && method.id === this.highlightedMethodId;
  }

  onAddNewClick(): void {
    OptimizelyService.trackEvent('billing_page_edit_payment_methods_button');
    this.isAddDialogShown = true;
  }

  async created(): Promise<void> {
    // The async/redirect add flow reloads the list itself in `mounted()`, so
    // skip the initial fetch here to avoid a redundant second round-trip.
    if (isReturningFromAsyncPaymentMethodAdd(this.$route.query)) {
      return;
    }

    await this.$store.dispatch('subscription/loadPaymentMethods');
  }

  async mounted(): Promise<void> {
    if (isReturningFromAsyncPaymentMethodAdd(this.$route.query)) {
      await this.handleAsyncPaymentMethodAddReturn();
    } else if (isFailedAsyncPaymentMethodAddReturn(this.$route.query)) {
      await this.handleFailedAsyncPaymentMethodAddReturn();
    } else if (this.$route.query.returningFromPaypal === 'true') {
      await clearAsyncPaymentMethodAddQueryParams(this.$router, this.$route);
    }

    await this.tryOpenPaymentEditFromQuery();
  }

  /**
   * Redirect-based adds that did not succeed (user aborted, provider rejected,
   * etc.) previously only cleared the URL, which looked like a silent no-op.
   * Surface a non-destructive snackbar first, then strip the redirect params.
   */
  private async handleFailedAsyncPaymentMethodAddReturn(): Promise<void> {
    await this.$store.dispatch('layout/setSnackbar', {
      type: 'error',
      icon: 'forbidden-system',
      text: this.$t('fields.account.billing.payment.payment_methods_add_error'),
      isSnackbar: true,
      active: true,
    });

    await clearAsyncPaymentMethodAddQueryParams(this.$router, this.$route);
  }

  /**
   * Reloads the list and resolves the freshly added method in parallel (they
   * don't depend on each other), so the list renders as soon as the backend
   * responds instead of waiting on the Stripe setup-intent lookup. Uses
   * allSettled so a Stripe lookup failure cannot skip list load, URL cleanup,
   * or highlight fallback. The URL cleanup is fire-and-forget so it never
   * blocks the highlight animation.
   */
  private async handleAsyncPaymentMethodAddReturn(): Promise<void> {
    const [resolveResult] = await Promise.allSettled([
      resolveAddedPaymentMethodIdFromQuery(
        this.$route.query,
        this.$store.state.subscription.stripeService,
        this.$store.state.auth.user?.stripe_instance_country
      ),
      this.$store.dispatch('subscription/loadPaymentMethods'),
    ]);

    const addedMethodId = resolveResult.status === 'fulfilled' ? resolveResult.value : null;

    void clearAsyncPaymentMethodAddQueryParams(this.$router, this.$route);

    await this.highlightAddedPaymentMethod(addedMethodId);
  }

  private async highlightAddedPaymentMethod(methodId: string | null): Promise<void> {
    if (methodId) {
      const method =
        this.defaultPaymentMethod?.id === methodId
          ? this.defaultPaymentMethod
          : this.otherSavedPaymentMethods.find((other) => other.id === methodId);

      if (method) {
        await this.highlightNewMethod(method);
        return;
      }
    }

    await this.highlightLatestOtherSavedMethod();
  }

  private async tryOpenPaymentEditFromQuery(): Promise<void> {
    if (this.$route.query.openModal !== 'paymentEdit') {
      return;
    }

    this.isAddDialogShown = true;
  }

  @Emit('update-payment-address')
  onUpdatePaymentAddress(): void {
    // Opens the billing address dialog in ActiveSubscription.
  }

  async reloadPaymentMethods(): Promise<void> {
    await this.$store.dispatch('subscription/loadPaymentMethods', true);
  }

  async onPaymentMethodAdded(method: SavedPaymentMethod | null): Promise<void> {
    this.isAddDialogShown = false;

    // Redirect-based flows (e.g. PayPal) don't hand us the new method directly,
    // so fall back to reloading the list in that case only.
    if (!method?.id) {
      await this.reloadPaymentMethods();
      await this.highlightLatestOtherSavedMethod();
      return;
    }

    // Insert the new method into the existing list instead of re-fetching, so
    // the list isn't hidden behind a spinner.
    await this.$store.dispatch('subscription/addPaymentMethod', method);
    await this.highlightNewMethod(method);
  }

  /**
   * After an async/redirect-based add (e.g. PayPal) we don't receive the new
   * method directly. Once the list is reloaded, the freshly added method is the
   * most recently created entry in "Other saved methods" (sorted by
   * `created_at` descending), so expand that panel and highlight it.
   */
  private async highlightLatestOtherSavedMethod(): Promise<void> {
    const [latestMethod] = this.otherSavedPaymentMethods;
    if (!latestMethod) {
      return;
    }

    await this.highlightNewMethod(latestMethod);
  }

  private async highlightNewMethod(method: SavedPaymentMethod): Promise<void> {
    // If the new method landed in the (possibly collapsed) "other saved" list,
    // expand that accordion first and wait for the row to render/expand, so the
    // highlight animation is actually visible to the user instead of playing
    // behind a collapsed panel.
    if (this.otherSavedPaymentMethods.some((other) => other.id === method.id)) {
      this.otherSavedMethodsPanel = 0;
      await this.$nextTick();
    }

    this.highlightedMethodId = method.id;

    if (this.highlightTimeout !== null) {
      window.clearTimeout(this.highlightTimeout);
    }

    this.highlightTimeout = window.setTimeout(() => {
      this.highlightedMethodId = null;
      this.highlightTimeout = null;
    }, PaymentMethodList.HIGHLIGHT_DURATION_MS);
  }

  beforeDestroy(): void {
    if (this.highlightTimeout !== null) {
      window.clearTimeout(this.highlightTimeout);
      this.highlightTimeout = null;
    }
  }

  onRemovePaymentMethod(method: SavedPaymentMethod): void {
    if (method.display_only || method.is_default || method.id === this.defaultPaymentMethod?.id) {
      return;
    }

    this.paymentMethodToRemove = method;
    this.isRemoveDialogShown = true;
  }

  onSetDefaultPaymentMethod(method: SavedPaymentMethod): void {
    if (this.isSettingDefault || method.display_only) {
      return;
    }

    if (this.defaultPaymentMethod?.type === 'bank_transfer' || this.defaultPaymentMethod?.display_only) {
      this.pendingSetDefaultMethod = method;
      this.isChangeDefaultDialogShown = true;
      return;
    }

    void this.applySetDefaultPaymentMethod(method);
  }

  async onChangeDefaultConfirmed(method: SavedPaymentMethod): Promise<void> {
    const succeeded = await this.applySetDefaultPaymentMethod(method);
    if (succeeded) {
      this.closeChangeDefaultDialog();
    }
  }

  async applySetDefaultPaymentMethod(method: SavedPaymentMethod): Promise<boolean> {
    if (this.isSettingDefault) {
      return false;
    }

    this.settingDefaultMethodId = method.id;

    try {
      await this.$store.dispatch('subscription/setDefaultPaymentMethod', method.id);
      await this.$store.dispatch('layout/setSnackbar', {
        type: 'info',
        icon: 'info-system',
        text: this.$t('fields.account.billing.payment.payment_methods_set_default_success'),
        isSnackbar: true,
        active: true,
      });
      return true;
    } catch {
      await this.$store.dispatch('layout/setSnackbar', {
        type: 'error',
        icon: 'forbidden-system',
        text: this.$t('fields.account.billing.payment.payment_methods_set_default_error'),
        isSnackbar: false,
        active: true,
      });
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      return false;
    } finally {
      this.settingDefaultMethodId = null;
    }
  }

  closeRemoveDialog(): void {
    this.isRemoveDialogShown = false;
    this.paymentMethodToRemove = null;
  }

  closeChangeDefaultDialog(): void {
    if (this.isSettingDefault) {
      return;
    }

    this.isChangeDefaultDialogShown = false;
    this.pendingSetDefaultMethod = null;
  }
}
</script>
